import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Row, Col, Spinner, InputGroup } from 'react-bootstrap';
import { emailSettingsAPI } from '../../services/api';

function EmailSettingsTab({ message, setMessage }) {
  const [settings, setSettings] = useState({
    smtp_host: 'mail.smtp2go.com',
    smtp_port: 2525,
    smtp_user: '',
    smtp_password: '',
    mail_from: 'recurorder@2k-designs.com',
    mail_from_name: 'RecurOrder',
    admin_email: '',
    email_enabled: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordSet, setPasswordSet] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await emailSettingsAPI.getSettings();
      const data = response.data;
      setSettings({
        smtp_host: data.smtp_host || 'mail.smtp2go.com',
        smtp_port: data.smtp_port || 2525,
        smtp_user: data.smtp_user || '',
        smtp_password: '', // Never pre-fill password
        mail_from: data.mail_from || 'recurorder@2k-designs.com',
        mail_from_name: data.mail_from_name || 'RecurOrder',
        admin_email: data.admin_email || '',
        email_enabled: data.email_enabled || false
      });
      setPasswordSet(data.smtp_password_set || false);
      setTestEmail(data.admin_email || '');
    } catch (error) {
      console.error('Failed to load email settings:', error);
      setMessage({ type: 'danger', text: 'Failed to load email settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      // Validate required fields if email is enabled
      if (settings.email_enabled) {
        if (!settings.smtp_host || !settings.smtp_port) {
          setMessage({ type: 'warning', text: 'SMTP host and port are required' });
          return;
        }
        if (!settings.smtp_user) {
          setMessage({ type: 'warning', text: 'SMTP username is required' });
          return;
        }
        if (!settings.smtp_password && !passwordSet) {
          setMessage({ type: 'warning', text: 'SMTP password is required' });
          return;
        }
        if (!settings.mail_from) {
          setMessage({ type: 'warning', text: 'Mail From address is required' });
          return;
        }
      }

      await emailSettingsAPI.updateSettings(settings);
      setMessage({ type: 'success', text: 'Email settings saved successfully!' });
      
      // Reload to get fresh state
      loadSettings();
    } catch (error) {
      console.error('Failed to save email settings:', error);
      setMessage({ type: 'danger', text: 'Failed to save email settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      const response = await emailSettingsAPI.testConnection();
      if (response.data.success) {
        setMessage({ type: 'success', text: '✅ SMTP connection successful!' });
      } else {
        setMessage({ type: 'danger', text: `❌ Connection failed: ${response.data.error}` });
      }
    } catch (error) {
      console.error('Connection test failed:', error);
      setMessage({ type: 'danger', text: `❌ Connection failed: ${error.response?.data?.error || error.message}` });
    } finally {
      setTesting(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      setMessage({ type: 'warning', text: 'Please enter an email address' });
      return;
    }

    try {
      setSendingTest(true);
      const response = await emailSettingsAPI.sendTestEmail(testEmail);
      if (response.data.success) {
        setMessage({ type: 'success', text: `✅ Test email sent to ${testEmail}!` });
      } else {
        setMessage({ type: 'danger', text: `❌ Failed to send: ${response.data.error || 'Unknown error'}` });
      }
    } catch (error) {
      console.error('Send test email failed:', error);
      // Extract error message from various possible locations
      const errorMsg = error.response?.data?.error 
        || error.response?.data?.message 
        || error.message 
        || 'Unknown error';
      setMessage({ type: 'danger', text: `❌ Failed to send: ${errorMsg}` });
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <Card className="fade-in">
        <Card.Header>📧 Email Configuration</Card.Header>
        <Card.Body className="text-center">
          <Spinner animation="border" size="sm" />
          <span className="ms-2">Loading email settings...</span>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="fade-in">
      <Card.Header>
        📧 Email Configuration
        <small className="text-muted ms-2">SMTP settings for notifications</small>
      </Card.Header>
      <Card.Body>
        <Alert variant="info" className="mb-4">
          <strong>Email Notifications:</strong> Configure SMTP settings to enable email notifications 
          for auto-submitted orders and other system events.
        </Alert>

        <Form onSubmit={handleSave}>
          {/* Master Enable Switch */}
          <Card className="mb-4">
            <Card.Body>
              <Form.Check
                type="switch"
                id="email_enabled"
                name="email_enabled"
                label={<strong>Enable Email Notifications</strong>}
                checked={settings.email_enabled}
                onChange={handleChange}
              />
              <Form.Text className="text-muted">
                Turn this on to enable sending emails. All settings below must be configured correctly.
              </Form.Text>
            </Card.Body>
          </Card>

          {/* SMTP Server Settings */}
          <h5 className="mb-3">SMTP Server Settings</h5>
          <Row className="mb-3">
            <Col md={8}>
              <Form.Group>
                <Form.Label><strong>SMTP Server</strong></Form.Label>
                <Form.Control
                  type="text"
                  name="smtp_host"
                  value={settings.smtp_host}
                  onChange={handleChange}
                  placeholder="mail.smtp2go.com"
                />
                <Form.Text className="text-muted">
                  smtp2go server: mail.smtp2go.com
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label><strong>Port</strong></Form.Label>
                <Form.Select
                  name="smtp_port"
                  value={settings.smtp_port}
                  onChange={handleChange}
                >
                  <option value="2525">2525 (Recommended)</option>
                  <option value="587">587 (TLS)</option>
                  <option value="465">465 (SSL)</option>
                  <option value="25">25 (Standard)</option>
                  <option value="8025">8025 (Alternative)</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label><strong>SMTP Username</strong></Form.Label>
                <Form.Control
                  type="text"
                  name="smtp_user"
                  value={settings.smtp_user}
                  onChange={handleChange}
                  placeholder="Your smtp2go username"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  <strong>SMTP Password</strong>
                  {passwordSet && <span className="text-success ms-2">(Currently set)</span>}
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    name="smtp_password"
                    value={settings.smtp_password}
                    onChange={handleChange}
                    placeholder={passwordSet ? "Leave blank to keep current" : "Your smtp2go password"}
                  />
                  <Button 
                    variant="outline-secondary"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </Button>
                </InputGroup>
                <Form.Text className="text-muted">
                  {passwordSet ? "Leave blank to keep current password" : "Enter your smtp2go password or API key"}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <hr className="my-4" />

          {/* From Address Settings */}
          <h5 className="mb-3">From Address</h5>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label><strong>Mail From Address</strong></Form.Label>
                <Form.Control
                  type="email"
                  name="mail_from"
                  value={settings.mail_from}
                  onChange={handleChange}
                  placeholder="recurorder@2k-designs.com"
                />
                <Form.Text className="text-muted">
                  Must be a verified sender in smtp2go
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label><strong>Display Name</strong></Form.Label>
                <Form.Control
                  type="text"
                  name="mail_from_name"
                  value={settings.mail_from_name}
                  onChange={handleChange}
                  placeholder="RecurOrder"
                />
                <Form.Text className="text-muted">
                  Shown as sender name in emails
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <hr className="my-4" />

          {/* Admin Notification Settings */}
          <h5 className="mb-3">Admin Notifications</h5>
          <Row className="mb-4">
            <Col md={6}>
              <Form.Group>
                <Form.Label><strong>Admin Email Address</strong></Form.Label>
                <Form.Control
                  type="email"
                  name="admin_email"
                  value={settings.admin_email}
                  onChange={handleChange}
                  placeholder="admin@example.com"
                />
                <Form.Text className="text-muted">
                  Receives auto-submit notifications and alerts
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {/* Save Button */}
          <div className="d-flex gap-2 mb-4">
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                '💾 Save Settings'
              )}
            </Button>
          </div>
        </Form>

        <hr className="my-4" />

        {/* Test Section */}
        <h5 className="mb-3">Test Email Configuration</h5>
        <Alert variant="secondary">
          <p className="mb-3">
            After saving your settings, use these buttons to verify your configuration works correctly.
          </p>
          
          <div className="d-flex gap-2 flex-wrap align-items-end">
            <Button 
              variant="outline-primary" 
              onClick={handleTestConnection}
              disabled={testing || !settings.smtp_user || (!settings.smtp_password && !passwordSet)}
            >
              {testing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Testing...
                </>
              ) : (
                '🔌 Test SMTP Connection'
              )}
            </Button>

            <div className="d-flex gap-2 align-items-end">
              <Form.Control
                type="email"
                placeholder="test@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                style={{ width: '250px' }}
              />
              <Button 
                variant="outline-success" 
                onClick={handleSendTestEmail}
                disabled={sendingTest || !testEmail || !settings.smtp_user || (!settings.smtp_password && !passwordSet)}
              >
                {sendingTest ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Sending...
                  </>
                ) : (
                  '📤 Send Test Email'
                )}
              </Button>
            </div>
          </div>

          {(!settings.smtp_user || (!settings.smtp_password && !passwordSet)) && (
            <p className="text-muted mt-2 mb-0">
              <small>Enter SMTP username and password above, then save settings to enable testing.</small>
            </p>
          )}
        </Alert>

        {/* Help Section */}
        <Alert variant="light" className="mt-4">
          <h6>📚 smtp2go Setup Guide</h6>
          <ol className="mb-0">
            <li>Log in to your <a href="https://www.smtp2go.com" target="_blank" rel="noopener noreferrer">smtp2go account</a></li>
            <li>Go to <strong>Settings → SMTP Users</strong></li>
            <li>Create or copy your SMTP username and password</li>
            <li>Go to <strong>Settings → Sender Domains</strong></li>
            <li>Verify your domain or use a verified sender email</li>
            <li>Enter the credentials above and test the connection</li>
          </ol>
        </Alert>
      </Card.Body>
    </Card>
  );
}

export default EmailSettingsTab;
